import {
  Alert,
  Box,
  Button,
  Container,
  Snackbar,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import heroPoster from "../../assets/New/herobg.jpg";
import heroVideoF from "../../assets/New/heroVideoF.mp4";
import ExpertModal from "../../components/ExpertModal";

export default function HeroVideo() {
  const videoRef = useRef(null);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // 🔹 MODAL STATE
  const [openModal, setOpenModal] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // 🔹 FORM STATE
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleOpenModal = () => {
    setFormData({ name: "", phone: "", email: "" }); // ✅ reset
    setError("");
    setSuccess("");
    setOpenModal(true);
  };

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // 🔹 FORM HANDLERS
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.email) {
      showToast("Please fill all required fields.", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "18ccc987-c5ed-437a-98f9-11f8e285b672",
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          subject: "New Lead from Vision X Nexus Website",
        }),
      });

      const data = await res.json();

      if (data.success) {
        setOpenModal(false); // ✅ instant close
        showToast("Thank you! We’ll contact you shortly.", "success");
        setFormData({ name: "", phone: "", email: "" });
      } else {
        showToast("Submission failed. Please try again.", "error");
      }
    } catch {
      showToast("Network error. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 INPUT STYLE
  const luxuryInput = {
    "& .MuiOutlinedInput-root": {
      height: 54,
      borderRadius: "14px",
      backgroundColor: "var(--color-white)",
      fontSize: 14,
    },
  };

  // 🔹 BUTTON STYLE
  const luxuryButton = {
    height: 54,
    background: "var(--color-gold)",
    color: "#1c1c1c",
    borderRadius: "40px",
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    fontWeight: 600,
  };

  const heroVideos = [
    heroVideoF,
    // intro1,
    // intro2,
    // intro3,
    // intro4,
    // intro5,
    // intro6,
    // intro7,
    // intro8,
  ];

  const handleVideoEnd = () => {
    setCurrentVideo((prev) => (prev === heroVideos.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100svh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* VIDEO BACKGROUND */}

      {/* DEFAULT BACKGROUND IMAGE (shown while video loads) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${heroPoster})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "opacity 0.6s ease",
          opacity: videoLoaded ? 0 : 1,
          zIndex: 0,
        }}
      />

      <Box
        component="video"
        ref={videoRef}
        key={currentVideo}
        autoPlay
        muted
        playsInline
        onCanPlayThrough={() => setVideoLoaded(true)} // ✅ video ready
        onEnded={handleVideoEnd}
        onLoadedMetadata={() => {
          if (videoRef.current) {
            videoRef.current.currentTime = 8;
          }
        }}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1,
        }}
      >
        <source src={heroVideos[currentVideo]} type="video/mp4" />
      </Box>

      {/* OVERLAY */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "#000000",
          opacity: 0.45,
          transition: "background 0.3s, border-radius 0.3s, opacity 0.3s",
          // background:
          //   "linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.5) 100%)",
          zIndex: 1,
        }}
      />

      {/* CONTENT */}
      <Container
        maxWidth="md"
        sx={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          px: { xs: 2, md: 0 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <Typography
            sx={{
              color: "var(--color-white)",
              fontWeight: 600,
              lineHeight: 1.1,
              fontSize: {
                xs: "2rem",
                sm: "2.8rem",
                md: "3.6rem",
              },
              textShadow: "0 25px 50px rgba(0,0,0,0.45)",
            }}
          >
            Vision
            <Box component="span" sx={{ color: "var(--color-gold)" }}>
              {" "}
              X
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#fff",
              maxWidth: 620,
              mx: "auto",
              fontSize: {
                xs: 14,
                sm: 15,
              },
              lineHeight: 1.8,
              mb: { xs: 4, sm: 5 },
              opacity: 0.95,
            }}
          >
            Where Strategy Meets Property
          </Typography>
          {/* <Button
            onClick={handleOpenModal}
            sx={{
              height: 56,
              px: 5,
              background: "var(--color-gold)",
              color: "var(--color-primary-dark)",
              borderRadius: "40px",
              fontSize: 13,
              letterSpacing: "0.18em",
              fontWeight: 600,
              textTransform: "uppercase",
              boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
              transition: "all 0.35s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 30px 70px rgba(0,0,0,0.6)",
              },
            }}
          >
            Speak With An Expert
          </Button> */}
          <Button
            onClick={handleOpenModal}
            sx={{
              height: { xs: 38, sm: 40, md: 42 },
              px: { xs: 3, sm: 4 },
              background: "var(--color-gold)",
              color: "var(--color-primary-dark)",
              borderRadius: "22px",
              fontSize: { xs: 10.5, sm: 11 },
              letterSpacing: { xs: "0.1em", sm: "0.12em" },
              fontWeight: 600,
              textTransform: "uppercase",
              boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
              transition: "all 0.25s ease",
              "&:hover": {
                transform: { md: "translateY(-1px)" },
                boxShadow: "0 14px 36px rgba(0,0,0,0.45)",
                background: "var(--color-gold)",
              },
            }}
          >
            Speak With An Expert
          </Button>
        </motion.div>
      </Container>
      <ExpertModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        loading={loading}
        error={error}
        success={success}
        luxuryInput={luxuryInput}
        luxuryButton={luxuryButton}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={2000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          sx={{
            borderRadius: "14px",
            fontSize: 14,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
